import "./base.css";
import "./polyfills/promise.polyfill";

import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import retainCriticalData from "./app/retainCriticalData";
import store from "./app/store";
import Application from "./features/application/Application";
import { NavigationVisibilityProvider } from "./features/navigation/NavigationVisibilityProvider";
import isOneWebDomainRuntime from "./shared/avaAppRuntime";
import { AVA_APP_NAME } from "./shared/constants";
import { cleanupLogger } from "./shared/Logger";

// Type guard to ensure container element exists
const getContainer = (): HTMLElement => {
    const container = isOneWebDomainRuntime()
        ? document.querySelector("main")
        : document.getElementById("root");

    if (!container) {
        throw new Error(
            isOneWebDomainRuntime()
                ? "Could not find <main> element for One Web Domain runtime"
                : "Could not find element with id 'root'"
        );
    }

    return container as HTMLElement;
};

// Initialize page hide event listener for logger cleanup (all runtimes)
window.addEventListener("pagehide", () => {
    // Retain critical data only for One Web Domain runtime (preserve original behavior)
    if (isOneWebDomainRuntime()) {
        retainCriticalData();
    }
    // Cleanup logger for all runtimes
    cleanupLogger();
});

// Initialize One Web Domain specific scripts
if (isOneWebDomainRuntime()) {
    import("./scripts/custom-elements");
    import("./scripts/qualtrics-tracking");
    import("./scripts/storage-utils");
    import("./scripts/retain-data");
}

// Initialize React application
const container: HTMLElement = getContainer();

// Add classes to main element when running in One Web Domain runtime
if (isOneWebDomainRuntime()) {
    container.classList.add("main-content", "margin-top-lg");
}

const basename = isOneWebDomainRuntime() ? AVA_APP_NAME : "";
const root: Root = createRoot(container);

root.render(
    <React.StrictMode>
        <Provider store={store}>
            <HelmetProvider>
                <NavigationVisibilityProvider>
                    <BrowserRouter basename={basename}>
                        <Application />
                    </BrowserRouter>
                </NavigationVisibilityProvider>
            </HelmetProvider>
        </Provider>
    </React.StrictMode>
);
